import { useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { LUX_PLUS_GIFT_CARD_CAMPAIGN } from 'luxPlus/constants/base'
import { dateIsBefore, dateIsAfterOrSame } from 'lib/datetime/dateUtils'
import { isBannerDismissed, recordSiteBannerDismissal } from 'storage/siteBanner'
import { useIsBannerHidden } from 'hooks/useIsBannerHidden'
import useSwitchRegion from 'hooks/useSwitchRegion'

const BANNER_ID = 'luxPlusGiftCardOfferBanner'

function useLuxPlusGiftCardBanner() {
  const currentRegionCode = useAppSelector(state => state.geo.currentRegionCode)
  const currentRegionCodeLower = currentRegionCode.toLowerCase()
  const memberSubscriptionItem = useAppSelector(state => state.auth.account.luxPlus.member.subscription.item)
  const canRedeemLuxPlusBenefits = useAppSelector(getCanRedeemLuxPlusBenefits)
  const isHidden = useIsBannerHidden('luxPlusGiftCardBanner')

  const now = new Date()
  const isCampaignActive = dateIsAfterOrSame(now, LUX_PLUS_GIFT_CARD_CAMPAIGN.START_DATE) &&
    dateIsBefore(now, LUX_PLUS_GIFT_CARD_CAMPAIGN.END_DATE)

  const [isDismissed, setIsDismissed] = useState(false)

  useEffect(() => {
    setIsDismissed(isBannerDismissed(BANNER_ID))
  }, [])

  const onDismiss = useCallback(() => {
    setIsDismissed(true)
    recordSiteBannerDismissal(BANNER_ID, LUX_PLUS_GIFT_CARD_CAMPAIGN.END_DATE.toISOString())
  }, [])

  const intl = useIntl()
  const memberRegion = memberSubscriptionItem?.region?.toLowerCase()
  const [, handleSwitchRegion] = useSwitchRegion(memberRegion)

  const isCurrentMemberRegion = canRedeemLuxPlusBenefits && memberRegion && (memberRegion === currentRegionCodeLower)

  let ctaLabel: string
  let ctaTo: string | undefined
  let ctaHref: string | undefined
  let ctaOnClick: (() => void) | undefined

  if (isCurrentMemberRegion) {
    ctaLabel = intl.formatMessage({
      id: 'JM4slQK4VQ',
      defaultMessage: 'View details',
      description: 'CTA button label for LuxPlus+ gift card banner when member is in their home region',
    })
    ctaTo = '/campaign/luxplus-gift-card-exclusive-savings'
  } else if (!!memberRegion && (currentRegionCodeLower !== memberRegion)) {
    ctaLabel = intl.formatMessage({
      id: 'S1xi1qeT5Z',
      defaultMessage: 'Switch to home region',
      description: 'CTA button label for LuxPlus+ gift card banner when member is in a different region',
    })
    ctaHref = `/${memberRegion}/campaign/luxplus-gift-card-exclusive-savings`
    ctaOnClick = handleSwitchRegion
  } else {
    ctaLabel = intl.formatMessage({
      id: 'BvxH31GMIR',
      defaultMessage: 'Become a member',
      description: 'CTA button label for LuxPlus+ gift card banner for non-members',
    })
    ctaTo = '/lux-plus?luxplusExclusiveOffer=true'
  }

  const showBanner = isCampaignActive && !isDismissed && !isHidden

  return useMemo(() => ({
    showBanner,
    onDismiss,
    ctaLabel,
    ctaTo,
    ctaHref,
    ctaOnClick,
  }), [ctaHref, ctaLabel, ctaOnClick, ctaTo, onDismiss, showBanner])
}

export default useLuxPlusGiftCardBanner
